import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ShoppingCart, AlertTriangle, TrendingDown, DollarSign,
  Calendar, RefreshCw, Loader2, ChevronLeft, ChevronRight,
  MapPin, Car, User, Mail, CreditCard, Filter,
  ArrowUpRight
} from 'lucide-react';

const API_BASE = window.location.origin || '';

const AdminAbandonedCheckoutsPage = () => {
  const navigate = useNavigate();
  const [checkouts, setCheckouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionTypes, setActionTypes] = useState([]);
  
  // Filters
  const [selectedAction, setSelectedAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statsDays, setStatsDays] = useState(30);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStats = async () => {
    try {
      const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API_BASE}/api/admin/abandoned-checkouts/stats?days=${statsDays}`, { headers });
      
      if (res.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const fetchCheckouts = async () => {
    setIsLoading(true);
    try {
      const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };
      
      let url = `${API_BASE}/api/admin/abandoned-checkouts?page=${page}&limit=20`;
      if (selectedAction) url += `&status=${selectedAction}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const res = await fetch(url, { headers });
      
      if (res.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await res.json();
      if (data.success) {
        setCheckouts(data.checkouts);
        setTotalPages(data.pages);
        setTotal(data.total);
        setActionTypes(data.action_types || []);
      }
    } catch (error) {
      console.error('Fetch checkouts error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchStats();
    fetchCheckouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedAction, dateFrom, dateTo, statsDays]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const getActionBadge = (action) => {
    const badges = {
      'payment_failed': { label: 'Ödeme Başarısız', color: 'bg-red-500/20 text-red-400' },
      'checkout_abandoned': { label: 'Terk Edildi', color: 'bg-yellow-500/20 text-yellow-400' },
      'checkout_expired': { label: 'Süresi Doldu', color: 'bg-orange-500/20 text-orange-400' },
      'paypal_checkout_abandoned': { label: 'PayPal Terk', color: 'bg-blue-500/20 text-blue-400' },
      'stripe_checkout_abandoned': { label: 'Stripe Terk', color: 'bg-purple-500/20 text-purple-400' },
      'webhook_signature_failed': { label: 'Webhook Hata', color: 'bg-gray-500/20 text-gray-400' },
      'FRAUD_DETECTED': { label: 'Fraud Şüphesi', color: 'bg-red-600/30 text-red-300 font-semibold' },
    };
    const badge = badges[action] || { label: action, color: 'bg-gray-500/20 text-gray-400' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const clearFilters = () => {
    setSelectedAction('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme" data-testid="abandoned-checkouts-page">
      <AdminSidebar />
      
      <div className="flex-1 lg:ml-64">
        <div className="p-4 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold">Terk Edilmiş Ödemeler</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Tamamlanmamış ödeme girişimlerini ve kaybedilen potansiyel geliri izleyin
              </p>
            </div>
            <Button
              onClick={() => { fetchStats(); fetchCheckouts(); }}
              variant="outline"
              size="sm"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Yenile
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Toplam Terk</p>
                      <p className="text-2xl font-bold">{stats.total_abandoned}</p>
                      <p className="text-xs text-muted-foreground">Son {stats.period_days} gün</p>
                    </div>
                    <ShoppingCart className="h-8 w-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Kayıp Gelir (Tahmini)</p>
                      <p className="text-2xl font-bold text-red-500">{formatCurrency(stats.estimated_lost_revenue)}</p>
                      <p className="text-xs text-muted-foreground">Geri kazanılabilir</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Terk Oranı</p>
                      <p className="text-2xl font-bold">{stats.abandonment_rate}%</p>
                      <p className="text-xs text-muted-foreground">
                        {stats.completed_bookings} tamamlanan
                      </p>
                    </div>
                    <TrendingDown className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Fraud Girişimi</p>
                      <p className="text-2xl font-bold text-red-600">{stats.fraud_attempts}</p>
                      <p className="text-xs text-muted-foreground">Engellendi</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Breakdown by Type */}
          {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
            <Card className="mb-6">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium">Terk Türüne Göre Dağılım</CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center gap-2">
                      {getActionBadge(type)}
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtreler:</span>
                </div>
                
                <select
                  value={selectedAction}
                  onChange={(e) => { setSelectedAction(e.target.value); setPage(1); }}
                  className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground"
                  data-testid="action-filter"
                >
                  <option value="">Tüm Türler</option>
                  {actionTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                    className="w-36 bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground [color-scheme:dark]"
                    placeholder="Başlangıç"
                  />
                  <span className="text-muted-foreground">-</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                    className="w-36 bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground [color-scheme:dark]"
                    placeholder="Bitiş"
                  />
                </div>

                {(selectedAction || dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    Temizle
                  </Button>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">İstatistik Periyodu:</span>
                  <select
                    value={statsDays}
                    onChange={(e) => setStatsDays(Number(e.target.value))}
                    className="bg-secondary border border-border rounded-md px-2 py-1 text-sm text-foreground"
                  >
                    <option value={7}>7 gün</option>
                    <option value={30}>30 gün</option>
                    <option value={90}>90 gün</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Checkouts List */}
          <Card>
            <CardHeader className="py-3 border-b">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm font-medium">
                  Terk Edilmiş Ödeme Girişimleri ({total})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : checkouts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Terk edilmiş ödeme bulunamadı</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {checkouts.map((checkout, idx) => (
                    <div key={idx} className="p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Left: Main Info */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            {getActionBadge(checkout.action)}
                            <span className="text-xs text-muted-foreground">
                              {formatDate(checkout.timestamp)}
                            </span>
                            {checkout.booking_ref && (
                              <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                                {checkout.booking_ref}
                              </span>
                            )}
                          </div>

                          {/* Route Info */}
                          {checkout.route_info && (
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-4 w-4 text-green-500" />
                                <span>{checkout.route_info.pickup || 'N/A'}</span>
                              </div>
                              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-4 w-4 text-red-500" />
                                <span>{checkout.route_info.dropoff || 'N/A'}</span>
                              </div>
                            </div>
                          )}

                          {/* Additional Details */}
                          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            {checkout.route_info?.vehicle_type && (
                              <div className="flex items-center gap-1">
                                <Car className="h-3 w-3" />
                                <span>{checkout.route_info.vehicle_type}</span>
                              </div>
                            )}
                            {checkout.route_info?.vehicle_price > 0 && (
                              <div className="flex items-center gap-1">
                                <CreditCard className="h-3 w-3" />
                                <span className="font-medium text-foreground">
                                  {formatCurrency(checkout.route_info.vehicle_price)}
                                </span>
                              </div>
                            )}
                            {checkout.route_info?.trip_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>{checkout.route_info.trip_date}</span>
                                {checkout.route_info.trip_time && (
                                  <span>{checkout.route_info.trip_time}</span>
                                )}
                              </div>
                            )}
                            {checkout.route_info?.passengers && (
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                <span>{checkout.route_info.passengers} yolcu</span>
                              </div>
                            )}
                            {checkout.route_info?.customer_email && (
                              <div className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                <span>{checkout.route_info.customer_email}</span>
                              </div>
                            )}
                          </div>

                          {/* Error message if exists */}
                          {checkout.error && (
                            <div className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded mt-1">
                              Hata: {checkout.error}
                            </div>
                          )}
                        </div>

                        {/* Right: Session ID */}
                        {checkout.session_id && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Session</p>
                            <p className="text-xs font-mono">{checkout.session_id.slice(0, 12)}...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Sayfa {page} / {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminAbandonedCheckoutsPage;
